import { jsPDF } from "jspdf";
import { Lexer } from "marked";

import { ViewerArtifacts } from "../../../data/queries/viewer";
import { formatDate } from "../../../utils/date";
import { ENLIGHTEN_LOGO_BASE64 } from "../../../utils/logo-asset";
import {
  capitalizeFirst,
  FONT_SIZES,
  getPageDimensions,
  LINE_HEIGHT,
  PAGE_MARGIN,
  PDF_COLORS,
  PdfContext,
  renderHeaderFooterOnAllPages,
  renderMarkdownTokens,
  renderSectionHeader,
  renderSubSection,
  setFont,
  SPACING,
  TEXT_WRAP_WIDTH
} from "../../../utils/pdf";

const PERFORMANCE_THRESHOLDS: Array<{ min: number; label: string }> = [
  { min: 4, label: "Advanced" },
  { min: 3, label: "Proficient" },
  { min: 2, label: "Improving" },
  { min: 0, label: "Needs Work" }
];

const INSIGHT_COLOR_MAP: Record<string, Record<string, { r: number; g: number; b: number }>> = {
  Sentiment: {
    positive: PDF_COLORS.green,
    neutral: PDF_COLORS.yellow,
    negative: PDF_COLORS.red
  },
  Relevance: {
    high: PDF_COLORS.green,
    medium: PDF_COLORS.yellow,
    low: PDF_COLORS.blue
  },
  Risk: {
    low: PDF_COLORS.green,
    medium: PDF_COLORS.yellow,
    high: PDF_COLORS.red
  },
  Performance: {
    advanced: PDF_COLORS.green,
    proficient: PDF_COLORS.blue,
    improving: PDF_COLORS.yellow,
    "needs work": PDF_COLORS.red
  }
};

interface CallEvent {
  isInternal: boolean;
  summary: {
    header: string;
    outcome: string;
  };
  parties: Array<{
    displayValue: string;
  }>;
  startedAt: string;
  duration: number;
  durationText: string;
}

interface Participant {
  name: string;
  role: string;
  company?: string;
}

interface PdfGenerationParams {
  data: ViewerArtifacts | null;
  selectedElements: string[];
  callEvent: CallEvent;
  interactionId: string;
}

const getPerformanceLevel = (score: number | undefined): string => {
  if (!score) {
    return "N/A";
  }
  const threshold = PERFORMANCE_THRESHOLDS.find(t => score >= t.min);
  return threshold?.label ?? "N/A";
};

const getInsightColor = (label: string, value: string): { r: number; g: number; b: number } => {
  return INSIGHT_COLOR_MAP[label]?.[value.toLowerCase()] ?? PDF_COLORS.grey;
};

const generateFileName = (header: string): string => {
  const sanitized = header
    .replace(/[^a-z0-9]/gi, "-")
    .replace(/-+/g, "-")
    .toLowerCase();
  return `call-${sanitized}-${Date.now()}.pdf`;
};

const addHeaderToPdf = (params: {
  doc: jsPDF;
  callEvent: CallEvent;
  yPosition: number;
  interactionId: string;
  data: ViewerArtifacts | null;
}): number => {
  const { doc, callEvent, yPosition, interactionId, data } = params;
  let currentY = yPosition;

  const viewUrl = `${window.location.origin}/viewer?interaction_id=${interactionId}`;

  setFont(doc, FONT_SIZES.link, "normal");
  doc.setTextColor(0, 102, 204);
  doc.textWithLink("View this interaction online", PAGE_MARGIN.left, currentY, { url: viewUrl });
  doc.setTextColor(0, 0, 0);
  currentY += SPACING.headerLink;

  setFont(doc, FONT_SIZES.title, "bold");
  doc.text(callEvent.summary.header, PAGE_MARGIN.left, currentY);
  currentY += SPACING.headerTitle;

  setFont(doc, FONT_SIZES.body, "normal");
  const formattedDate = callEvent.startedAt
    ? formatDate(new Date(callEvent.startedAt), "dd MMMM yyyy @ h:mm a")
    : "";
  const datePrefix = formattedDate ? `Called ${formattedDate}. ` : "";
  doc.text(`${datePrefix}${callEvent.summary.outcome}`, PAGE_MARGIN.left, currentY);
  currentY += SPACING.sectionLarge;

  currentY = renderInsights({ doc, data, currentY });

  return currentY + SPACING.sectionLarge;
};

const renderInsights = (params: {
  doc: jsPDF;
  data: ViewerArtifacts | null;
  currentY: number;
}): number => {
  const { doc, data } = params;
  let { currentY } = params;

  const sentiment = data?.summary?.content?.sentiment;
  const relevance = data?.summary?.content?.relevance;
  const risk = data?.riskAnalysis?.content?.riskAnalysis?.riskAnalysis?.overallIndicator ?? "N/A";
  const performanceScore = data?.scorecard?.content?.scorecard?.overall_score;
  const performanceLevel = getPerformanceLevel(performanceScore);

  const insights = [
    { label: "Sentiment", value: sentiment ?? "N/A" },
    { label: "Relevance", value: relevance ?? "N/A" },
    { label: "Risk", value: risk },
    { label: "Performance", value: performanceLevel }
  ];

  const DOT_RADIUS = 1.2;
  const DOT_OFFSET = 5;
  const DOT_VERTICAL_OFFSET = 1.2;
  const LABEL_VALUE_GAP = 40;

  setFont(doc, FONT_SIZES.body, "normal");

  insights.forEach((insight) => {
    const color = getInsightColor(insight.label, insight.value);
    doc.setFillColor(color.r, color.g, color.b);
    doc.circle(PAGE_MARGIN.left + DOT_RADIUS, currentY - DOT_VERTICAL_OFFSET, DOT_RADIUS, "F");

    setFont(doc, FONT_SIZES.body, "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(`${insight.label}:`, PAGE_MARGIN.left + DOT_OFFSET, currentY);

    setFont(doc, FONT_SIZES.body, "normal");
    const displayValue = typeof insight.value === "string"
      ? capitalizeFirst(insight.value)
      : String(insight.value);
    doc.text(displayValue, PAGE_MARGIN.left + DOT_OFFSET + LABEL_VALUE_GAP, currentY);

    currentY += LINE_HEIGHT.normal;
  });

  doc.setTextColor(0, 0, 0);
  return currentY;
};

const addSummaryToPdf = (params: {
  doc: jsPDF;
  data: ViewerArtifacts | null;
  yPosition: number;
}): number => {
  const { doc, data, yPosition } = params;
  const { pageWidth, maxY } = getPageDimensions(doc);

  const context: PdfContext = { doc, currentY: yPosition, maxY, pageWidth };
  let currentY = renderSectionHeader(context, "Summary");

  const summary = data?.summary?.content;
  if (!summary) {
    setFont(doc, FONT_SIZES.body, "normal");
    doc.text("No summary available", PAGE_MARGIN.left, currentY);
    return currentY + SPACING.sectionLarge;
  }

  currentY = renderSubSection({ doc, title: "Intent", content: summary.intent ?? "N/A", currentY });
  currentY = renderSubSection({ doc, title: "Summary", content: summary.summary ?? "N/A", currentY });

  if (summary.next_step) {
    const nextStepText = `${summary.next_step.timeframe}: ${summary.next_step.description}`;
    currentY = renderSubSection({ doc, title: "Next Steps", content: nextStepText, currentY });
  }

  if (summary.participants && summary.participants.length > 0) {
    setFont(doc, FONT_SIZES.subHeader, "bold");
    doc.text("Participants", PAGE_MARGIN.left, currentY);
    currentY += LINE_HEIGHT.normal;

    setFont(doc, FONT_SIZES.body, "normal");
    summary.participants.forEach((participant: Participant) => {
      const companyText = participant.company ? ` at ${participant.company}` : "";
      doc.text(`${participant.name} - ${participant.role}${companyText}`, PAGE_MARGIN.left, currentY);
      currentY += LINE_HEIGHT.normal;
    });
    currentY += SPACING.section;
  }

  return currentY + SPACING.section;
};

const addTranscriptToPdf = (params: {
  doc: jsPDF;
  data: ViewerArtifacts | null;
  yPosition: number;
}): number => {
  const { doc, data, yPosition } = params;
  const { pageWidth, maxY } = getPageDimensions(doc);

  const context: PdfContext = { doc, currentY: yPosition, maxY, pageWidth };
  let currentY = renderSectionHeader(context, "Transcript");

  const transcriptText = data?.transcript?.content?.transcription ?? "";
  if (!transcriptText) {
    setFont(doc, FONT_SIZES.body, "normal");
    doc.text("No transcript available", PAGE_MARGIN.left, currentY);
    return currentY + SPACING.sectionLarge;
  }

  setFont(doc, FONT_SIZES.body, "normal");

  transcriptText.split("\n").forEach((line: string) => {
    if (currentY > maxY) {
      doc.addPage();
      currentY = PAGE_MARGIN.top;
    }

    const speakerMatch = line.match(/^(\[.*?\]:)\s*/);
    if (!speakerMatch) {
      setFont(doc, FONT_SIZES.body, "normal");
      const wrappedLines = doc.splitTextToSize(line, TEXT_WRAP_WIDTH);
      doc.text(wrappedLines, PAGE_MARGIN.left, currentY);
      currentY += wrappedLines.length * LINE_HEIGHT.transcript;
      return;
    }

    currentY = renderTranscriptLine({ doc, speakerMatch, line, startY: currentY, maxY });
  });

  return currentY + SPACING.section;
};

const renderTranscriptLine = (params: {
  doc: jsPDF;
  speakerMatch: RegExpMatchArray;
  line: string;
  startY: number;
  maxY: number;
}): number => {
  const { doc, speakerMatch, line, startY, maxY } = params;
  let currentY = startY;
  const speaker = speakerMatch[1];
  const content = line.substring(speakerMatch[0].length);

  setFont(doc, FONT_SIZES.body, "bold");
  const speakerWidth = doc.getTextWidth(speaker + " ");
  doc.text(speaker, PAGE_MARGIN.left, currentY);

  setFont(doc, FONT_SIZES.body, "normal");
  const contentWrapWidth = TEXT_WRAP_WIDTH - speakerWidth;
  const wrappedContent = doc.splitTextToSize(content, contentWrapWidth);

  doc.text(wrappedContent[0] ?? "", PAGE_MARGIN.left + speakerWidth, currentY);
  currentY += LINE_HEIGHT.transcript;

  for (let i = 1; i < wrappedContent.length; i++) {
    if (currentY > maxY) {
      doc.addPage();
      currentY = PAGE_MARGIN.top;
    }
    doc.text(wrappedContent[i], PAGE_MARGIN.left, currentY);
    currentY += LINE_HEIGHT.transcript;
  }

  return currentY;
};

const addExtendedSummaryToPdf = (params: {
  doc: jsPDF;
  data: ViewerArtifacts | null;
  yPosition: number;
}): number => {
  const { doc, data, yPosition } = params;

  const summary = data?.summary?.content;
  if (!summary?.extendedSummary) {
    return yPosition;
  }

  const { pageWidth, maxY } = getPageDimensions(doc);
  const context: PdfContext = { doc, currentY: yPosition, maxY, pageWidth };
  let currentY = renderSectionHeader(context, "Extended Summary");

  const tokens = Lexer.lex(summary.extendedSummary);

  currentY = renderMarkdownTokens(tokens, {
    doc,
    currentY,
    maxY,
    baseIndent: PAGE_MARGIN.left
  });

  return currentY + SPACING.sectionLarge;
};

type SectionRenderer = (params: { doc: jsPDF; data: ViewerArtifacts | null; yPosition: number }) => number;

const sectionRenderers: Record<string, SectionRenderer> = {
  summary: addSummaryToPdf,
  extendedSummary: addExtendedSummaryToPdf,
  transcript: addTranscriptToPdf
};

export const generatePdf = (params: PdfGenerationParams): void => {
  const { data, selectedElements, callEvent, interactionId } = params;
  const doc = new jsPDF();

  let yPosition = PAGE_MARGIN.top;

  yPosition = addHeaderToPdf({ doc, callEvent, yPosition, interactionId, data });

  selectedElements.forEach((element) => {
    const renderer = sectionRenderers[element];
    if (renderer) {
      yPosition = renderer({ doc, data, yPosition });
    }
  });

  renderHeaderFooterOnAllPages(doc, ENLIGHTEN_LOGO_BASE64);

  doc.save(generateFileName(callEvent.summary.header));
};
